import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { NewsItem } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const emptyForm = { imageUrl: '', dateLabel: '', title: '', excerpt: '', content: '' };

const NewsEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useScript();
  const { token } = useAuth();
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [titleError, setTitleError] = useState('');

  const isEdit = Boolean(id);
  useDocumentTitle(isEdit ? 'Уређивање вести — Манастир Градац' : 'Нова вест — Манастир Градац');

  useEffect(() => {
    if (!id) return;
    api.getNewsById(Number(id))
      .then(item => setForm({
        imageUrl: item.imageUrl,
        dateLabel: item.dateLabel,
        title: item.title,
        excerpt: item.excerpt,
        content: item.content,
      }))
      .catch((err: ApiError) => setError(err?.message || t('Не могу учитати вест.')));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    // Client-side validation
    setTitleError('');
    if (!form.title.trim()) {
      setTitleError(t('Наслов је обавезан.'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      let result: NewsItem;
      if (isEdit && id) {
        result = await api.updateNews(token, Number(id), form);
        navigate(`/novosti/${result.id}`);
      } else {
        result = await api.createNews(token, form as Omit<NewsItem, 'id'>);
        navigate('/novosti');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr?.message || t('Грешка при чувању. Покушај поново.'));
      setSaving(false);
    }
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="news-edit-page">
      <form className="news-edit-layout" onSubmit={handleSubmit}>
        <div className="news-edit-form-col">
          <h1 className="news-heading" style={{ textAlign: 'left', marginBottom: '28px' }}>
            {isEdit ? t('Уреди вест') : t('Нова вест')}
          </h1>

          {error && <div className="form-error-banner" role="alert">{error}</div>}

          <ImageUpload
            label="URL slike"
            imageUrl={form.imageUrl}
            onImageUrlChange={url => setForm(prev => ({ ...prev, imageUrl: url }))}
          />

          <label className="post-admin-label">{t('Датум')}</label>
          <input
            className="news-edit-input"
            type="text"
            placeholder={t('нпр. 15. март 2026.')}
            value={form.dateLabel}
            onChange={set('dateLabel')}
          />

          <label className="post-admin-label">{t('Наслов')}</label>
          <input
            className="news-edit-input"
            type="text"
            value={form.title}
            onChange={e => {
              if (titleError) setTitleError('');
              set('title')(e);
            }}
            aria-invalid={Boolean(titleError)}
          />
          {titleError && <div className="field-error" role="alert">{titleError}</div>}

          <label className="post-admin-label">{t('Кратак текст (excerpt)')}</label>
          <textarea
            className="news-edit-textarea"
            rows={3}
            value={form.excerpt}
            onChange={set('excerpt')}
          />

          <label className="post-admin-label">{t('Пуни текст')}</label>
          <textarea
            className="news-edit-textarea"
            rows={15}
            value={form.content}
            onChange={set('content')}
          />

          {/* spacer so sticky bar doesn't cover last field */}
          <div style={{ height: '80px' }} />
        </div>

        <div className="news-edit-preview-col">
          <p className="post-admin-label" style={{ marginBottom: '10px' }}>{t('Предпреглед')}</p>
          <div className="news-edit-preview-card">
            {form.imageUrl && (
              <div className="news-image" style={{ aspectRatio: '16/9', height: 'auto' }}>
                <img
                  src={form.imageUrl}
                  alt={form.title}
                  width={800}
                  height={450}
                  decoding="async"
                />
                {form.dateLabel && <div className="news-date">{form.dateLabel}</div>}
              </div>
            )}
            {!form.imageUrl && (
              <div className="news-image news-image-placeholder">
                {form.dateLabel && <div className="news-date">{form.dateLabel}</div>}
              </div>
            )}
            <div className="news-body">
              <h3 className="news-title">{form.title || t('Наслов вести...')}</h3>
              <p className="news-excerpt">{form.excerpt || t('Кратак опис...')}</p>
            </div>
          </div>
        </div>

        <div className="news-edit-sticky-bar">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t('Чување...') : t('Сачувај')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            {t('Откажи')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewsEditPage;
